import {
  alpha,
  Box,
  Checkbox,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useLanguage } from "../../Language";
import { useIsMobile } from "@tlc/ui/useIsMobile";

export enum secondaryTypes {
  success = "success",
  error = "error",
}

interface SharedViewHeaderProps {
  labelKey: string;
  count: number;
  secondaryLabelKey?: string;
  secondaryCount?: number;
  secondaryType?: secondaryTypes;
  headerActions?: React.ReactNode;
  selectedActions?: React.ReactNode;
  showSelectAll?: boolean;
  loading?: boolean;
  sortControl?: React.ReactNode;
  selectedLength: number;
  isAllIndeterminate: boolean;
  isAllSelected: boolean;
  handleSelectAllChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
}

export function SharedViewHeader({
  labelKey,
  count,
  secondaryLabelKey,
  secondaryCount,
  secondaryType = secondaryTypes.error,
  headerActions,
  selectedActions,
  showSelectAll = true,
  loading,
  sortControl,
  selectedLength,
  isAllIndeterminate,
  isAllSelected,
  handleSelectAllChange,
}: SharedViewHeaderProps) {
  const { translate } = useLanguage();
  const theme = useTheme();
  const isMobile = useIsMobile();

  return (
    <>
      {selectedLength > 0 && (
        <Toolbar
          disableGutters
          sx={{
            flexWrap: "wrap",
            gap: 1,
            ml: 0,
            pl: 0,
            justifyContent: "space-between",
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity,
              ),
          }}
        >
          {showSelectAll && !isMobile && (
            <Box display="flex" flexDirection="row" alignItems="center">
              <Checkbox
                color="primary"
                indeterminate={isAllIndeterminate}
                checked={isAllSelected}
                disabled={loading}
                onChange={handleSelectAllChange}
                slotProps={{
                  input: {
                    "aria-label": translate("selectAll"),
                  },
                }}
              />
              <Typography variant="body2">
                {selectedLength} {translate("selected")}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: {
                xs: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity,
                  ),
                sm: "transparent",
              },
              borderRadius: { xs: 1, sm: 0 },
              px: { xs: 1, sm: 0 },
              py: { xs: 0.5, sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {selectedActions}

            <Box sx={{ flexGrow: 1 }} />
            {showSelectAll && isMobile && (
              <Checkbox
                color="primary"
                indeterminate={isAllIndeterminate}
                checked={isAllSelected}
                disabled={loading}
                onChange={handleSelectAllChange}
                slotProps={{
                  input: {
                    "aria-label": translate("selectAll"),
                  },
                }}
              />
            )}
          </Box>
        </Toolbar>
      )}

      {selectedLength === 0 && (
        <Toolbar
          disableGutters
          sx={{
            flexWrap: "wrap",
            px: 0,
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          {showSelectAll && !isMobile && (
            <Checkbox
              color="primary"
              indeterminate={isAllIndeterminate}
              checked={isAllSelected}
              disabled={loading}
              onChange={handleSelectAllChange}
              slotProps={{
                input: {
                  "aria-label": translate("selectAll"),
                },
              }}
            />
          )}
          <Typography
            sx={{
              flex: "1 1",
              textWrap: "nowrap",
              ml: showSelectAll && !isMobile ? 0 : 1,
            }}
            color="inherit"
            variant="h5"
            component="h2"
          >
            {translate(labelKey)}: {count}
            {secondaryLabelKey && (
              <Box
                component={"span"}
                sx={{
                  color:
                    secondaryCount && secondaryCount > 0
                      ? secondaryType === secondaryTypes.error
                        ? theme.palette.error.dark
                        : theme.palette.success.main
                      : "inherit",

                  ml: 1.5,
                  fontWeight:
                    secondaryCount && secondaryCount > 0 ? 600 : "inherit",
                }}
              >
                {`${translate(secondaryLabelKey)}: `}
                {secondaryCount}
              </Box>
            )}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1,
            }}
          >
            {headerActions}
            <Box component="div" sx={{ display: { xs: "none", sm: "block" } }}>
              {sortControl}
            </Box>
          </Box>
        </Toolbar>
      )}

      <Toolbar
        disableGutters
        sx={{
          justifyContent: "space-between",
          minHeight: "auto",
          py: 1,
          pl: isMobile ? 1 : 3,
          pr: 1, //LOL
          display: { xs: selectedLength > 0 ? "none" : "flex", sm: "none" },
        }}
      >
        {sortControl}
        {showSelectAll && (
          <Checkbox
            color="primary"
            indeterminate={isAllIndeterminate}
            checked={isAllSelected}
            disabled={loading}
            onChange={handleSelectAllChange}
            slotProps={{
              input: {
                "aria-label": translate("selectAll"),
              },
            }}
          />
        )}
      </Toolbar>
    </>
  );
}
